import React, { useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableSortLabel from '@mui/material/TableSortLabel';
import PaymentStatusChip from './../PaymentStatusChip';
import { Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { formatCurrency } from '../../utils/money';
import { PagamentoInscricao } from '../../types/pagamento';

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '' : '';

interface PaymentsTableProps {
  pagamentos: PagamentoInscricao[];
  loading?: boolean;
  onRefresh?: () => void;
  emptyMessage?: string;
}

const PaymentsTable: React.FC<PaymentsTableProps> = ({ pagamentos, loading, emptyMessage }) => {
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = () => setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));

  const sortedPagamentos = useMemo(() => {
    return pagamentos.slice().sort((a, b) => {
      const da = new Date(a.dataPagamento || a.criadoEm || '').getTime();
      const db = new Date(b.dataPagamento || b.criadoEm || '').getTime();
      return order === 'asc' ? da - db : db - da;
    });
  }, [pagamentos, order]);

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#6c757d' }}>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Atleta</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Meio de pagamento</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Valor</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Desconto</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Valor sem desconto</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Cupom</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
              <TableSortLabel active direction={order} onClick={handleSort} style={{ color: 'inherit' }}>
                Data do pagamento
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Código da transação</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Recibo</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Criado em</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, idx) => (
              <TableRow key={idx}>
                {Array.from({ length: 11 }).map((__, cidx) => (
                  <TableCell key={cidx}><Skeleton variant="text" /></TableCell>
                ))}
              </TableRow>
            ))
          ) : sortedPagamentos.length === 0 ? (
            <TableRow>
              <TableCell colSpan={11} align="center">
                {emptyMessage || 'Nenhum pagamento encontrado.'}
              </TableCell>
            </TableRow>
          ) : (
            sortedPagamentos.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{
                  p.inscricoes && p.inscricoes[0]?.equipe?.atletas?.[0]?.usuario?.nome
                    ? p.inscricoes[0]?.equipe?.atletas?.[0]?.usuario?.nome
                    : '-'
                }</TableCell>
                <TableCell><PaymentStatusChip status={p.status} /></TableCell>
                <TableCell>{p.meioPagamento || '-'}</TableCell>
                <TableCell>
                  {formatCurrency(p.valor ?? 0)}
                </TableCell>
                <TableCell>{p.descontoAplicado != null ? formatCurrency(p.descontoAplicado) : '-'}</TableCell>
                <TableCell>{p.valorSemDesconto != null ? formatCurrency(p.valorSemDesconto) : '-'}</TableCell>
                <TableCell>{p.cupomAplicado?.codigo || '-'}</TableCell>
                <TableCell>{formatDateTime(p.dataPagamento)}</TableCell>
                <TableCell>{p.codigoTransacao || '-'}</TableCell>
                <TableCell>
                  {p.reciboUrl ? (
                    <a href={p.reciboUrl} target="_blank" rel="noreferrer">Abrir recibo</a>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{formatDateTime(p.criadoEm)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PaymentsTable;
